import React from "react";

/**
 * Top-right hyperlinked image widget on the home page. Renders nothing
 * when the admin hasn't set an image URL yet (so the right column
 * collapses gracefully and the countdown card stretches up to fill
 * the gap).
 *
 * Props:
 *   - image: { image_url, link_url, alt } — exactly as returned by
 *     GET /api/settings/homepage-image.
 */
export default function HomeImageCard({ image }) {
  const url = (image && image.image_url) || "";
  if (!url) return null;
  const href = (image && image.link_url) || "";
  const alt = (image && image.alt) || "";

  const img = (
    <div className="w-full overflow-hidden">
      <img
        src={url}
        alt={alt}
        loading="lazy"
        decoding="async"
        className="block h-auto w-full transition-transform duration-500 hover:scale-[1.015]"
        data-testid="home-image-card-img"
      />
    </div>
  );

  const wrapperCls =
    "overflow-hidden rounded-md border border-black/10 bg-white";

  if (href) {
    // Default behaviour: internal links (/...) stay in-tab, external open
    // in a new tab. Admin can override either way via `open_in_new_tab`.
    const isExternal = !href.startsWith("/");
    const newTab = typeof image.open_in_new_tab === "boolean" ? image.open_in_new_tab : isExternal;
    return (
      <a
        href={href}
        target={newTab ? "_blank" : "_self"}
        rel={newTab ? "noopener noreferrer" : undefined}
        data-testid="home-image-card-link"
        className={wrapperCls + " block focus:outline-none focus:ring-2 focus:ring-[#6CABDD]"}
      >
        {img}
      </a>
    );
  }
  return (
    <div className={wrapperCls} data-testid="home-image-card">
      {img}
    </div>
  );
}
